/**
 * Visit-schedule form matching: the ONE rule for "is this value a CRF form?".
 *
 * Jim, item 5 (2026-10-01): a study's visit schedule carried dosing text in its
 * forms column ("Study drug 10 mg BID") instead of form names, so no visit-form
 * matrix could be built. The rule: every value in a visit's `forms` must match
 * a real CRF form, by
 *   1. exact name,
 *   2. normalised name (case and punctuation only, `normalizeFormName`),
 *   3. the form's code, or
 *   4. a person-made alias (`studyDesign/config.formAliases`, a label-to-form-
 *      name map written only when a person maps a label).
 *
 * NEVER substring and NEVER word overlap. "Physical Exam" is not "Physical
 * Examination": a wrong assignment silently corrupts the schedule, while an
 * unmatched label is kept in `formsToConfirm` for a person to decide. Nothing is
 * invented and nothing is dropped.
 *
 * A model answer (Gemini 3 can return a well-formed but wrong answer) is never
 * accepted as a form name unless it passes this matcher.
 *
 * CORRECTED 2026-10-29 (Jim, product owner). The rule above, "NEVER substring
 * and NEVER word overlap ... A model answer ... is never accepted as a form
 * name unless it passes this matcher", was added by an implementing session and
 * attributed to Jim. It was not his ruling. What is genuinely his (item 5,
 * 2026-10-01) is the first sentence: every assignment must name a REAL CRF form
 * of the study. His ruling of 2026-10-29 is that the matrix "should be filled
 * in from the protocol but it should be updatable by the user". This file stays
 * the pure, model-free STRICT matcher (it writes `visits[].forms`); a model's
 * proposals for the labels it cannot place live in schedule/visitMatrixFill.ts,
 * are checked against the real forms before they are kept, and are recorded
 * beside the visit in `modelMatches`, never in `forms`.
 *
 * Pure. No Firestore, no model.
 */
import { normalizeFormName } from "../utils/scheduleFormOrder";
import { matchLabelByWording, prepareForms, type WordingHow } from "./visitFormWording";

export interface CrfFormRef {
  name: string;
  code?: string | null;
  /** Person-made labels that mean this form (from studyDesign/config.formAliases). */
  aliases?: string[];
}

export type FormAliases = Record<string, string>;

export interface FormMatch {
  /** The value as it was given (trimmed). */
  label: string;
  /** The real CRF form name it resolves to. */
  form: string;
  how: "exact" | "normalised" | "code" | "alias" | WordingHow;
  /** For a parenthetical match, the item inside the label that matched. */
  item?: string;
}

/** A label two or more forms qualified for by wording: never guessed, kept to confirm. */
export interface AmbiguousLabel {
  label: string;
  candidates: string[];
}

export interface MatchResult {
  matched: FormMatch[];
  /** Labels that are not CRF form names, trimmed and de-duplicated. */
  failing: string[];
  /** Failing labels that were ambiguous by wording, with the forms that qualified. */
  ambiguous: AmbiguousLabel[];
}

/**
 * "no_schedule": the study has NO visits. Nothing is waiting, but nothing was
 * matched either; counting waiting labels alone read an empty schedule as
 * "matched" (PENN-KEEPCALM-R5: forms, 0 visits, "matched").
 */
export type ScheduleFormsStatus = "matched" | "to_be_confirmed" | "no_schedule";

export interface ScheduleFormsStatusFields {
  scheduleFormsStatus: ScheduleFormsStatus;
  /** Plain-language reason, present when status is to_be_confirmed. */
  scheduleFormsReason?: string;
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * Turn the study's `formAliases` map (label -> form name) into per-form alias
 * lists. An alias whose target is not a real form name (exact or normalised) is
 * ignored: an alias can only point at a form that exists.
 */
export function formsWithAliases(forms: CrfFormRef[], formAliases: FormAliases | null | undefined): CrfFormRef[] {
  const out = (forms ?? []).map((f) => ({ ...f, aliases: [...(f.aliases ?? [])] }));
  if (!formAliases || typeof formAliases !== "object") return out;
  for (const [label, target] of Object.entries(formAliases)) {
    const l = str(label);
    const t = str(target);
    if (!l || !t) continue;
    const form =
      out.find((f) => f.name === t) ??
      out.find((f) => normalizeFormName(f.name) === normalizeFormName(t));
    if (form) form.aliases!.push(l);
  }
  return out;
}

/** Build lookup tables once; a key shared by two forms is ambiguous and never resolves. */
function buildIndex(forms: CrfFormRef[]) {
  const exact = new Map<string, string>();
  const norm = new Map<string, string | null>();
  const code = new Map<string, string | null>();
  const alias = new Map<string, string | null>();
  const put = (m: Map<string, string | null>, k: string, name: string) => {
    if (!k) return;
    if (!m.has(k)) m.set(k, name);
    else if (m.get(k) !== name) m.set(k, null); // ambiguous
  };
  for (const f of forms ?? []) {
    const name = str(f?.name);
    if (!name) continue;
    if (!exact.has(name)) exact.set(name, name);
    put(norm, normalizeFormName(name), name);
    const c = str(f.code);
    if (c) put(code, normalizeFormName(c), name);
    for (const a of f.aliases ?? []) put(alias, normalizeFormName(str(a)), name);
  }
  return { exact, norm, code, alias };
}

/**
 * Match labels to real CRF forms. Blank labels are ignored; failing labels are
 * trimmed and de-duplicated (by normalised key, first spelling kept).
 */
export function matchVisitForms(labels: unknown[], forms: CrfFormRef[]): MatchResult {
  const idx = buildIndex(forms);
  const matched: FormMatch[] = [];
  const failing: string[] = [];
  const failingKeys = new Set<string>();
  const ambiguous: AmbiguousLabel[] = [];
  const wordingForms = prepareForms((forms ?? []).map((f) => str(f?.name)).filter(Boolean));
  /** The strict tiers, then wording, for one piece of text. */
  const tryText = (text: string): { found: Array<Omit<FormMatch, "label">>; ambiguous?: string[] } => {
    const k = normalizeFormName(text) || `raw:${text}`;
    const ex = idx.exact.get(text);
    if (ex) return { found: [{ form: ex, how: "exact" }] };
    const n = idx.norm.get(k);
    if (n) return { found: [{ form: n, how: "normalised" }] };
    const c = idx.code.get(k);
    if (c) return { found: [{ form: c, how: "code" }] };
    const a = idx.alias.get(k);
    if (a) return { found: [{ form: a, how: "alias" }] };
    const w = matchLabelByWording(text, wordingForms);
    if (w.matches.length) {
      return { found: w.matches.map((m) => ({ form: m.form, how: m.how, ...(m.item ? { item: m.item } : {}) })) as Array<Omit<FormMatch, "label">> };
    }
    return { found: [], ambiguous: w.ambiguous as string[] | undefined };
  };
  for (const raw of labels ?? []) {
    const label = str(raw);
    if (!label) continue;
    const k = normalizeFormName(label) || `raw:${label}`;
    const whole = tryText(label);
    if (whole.found.length) {
      for (const m of whole.found) matched.push({ label, ...m } as FormMatch);
      continue;
    }
    // A qualifier ("..., if applicable") or a "//" separator around a label that
    // otherwise names a form. Each variant goes through the SAME strict tiers;
    // nothing is loosened.
    const viaVariants: Array<Omit<FormMatch, "label">> = [];
    for (const v of labelVariants(label)) {
      for (const m of tryText(v).found) if (!viaVariants.some((x) => x.form === m.form)) viaVariants.push(m);
    }
    if (viaVariants.length) {
      for (const m of viaVariants) matched.push({ label, ...m } as FormMatch);
      continue;
    }
    if (!failingKeys.has(k)) {
      failingKeys.add(k);
      failing.push(label);
      if (whole.ambiguous) ambiguous.push({ label, candidates: whole.ambiguous });
    }
  }
  return { matched, failing, ambiguous };
}

/** A trailing qualifier that says WHEN, not WHICH form. */
const TRAILING_QUALIFIER = /\s*(?:[,;:\-–—]\s*|\(\s*)(?:if (?:applicable|indicated|needed|required|appropriate)|as (?:needed|applicable|indicated)|when (?:applicable|indicated))\s*\)?\s*$/i;

/**
 * The texts to try when the whole label names no form: each side of a "//"
 * separator, each with a trailing "if applicable"-style qualifier removed. Never
 * the label itself, and never a partial-word variant.
 */
export function labelVariants(label: string): string[] {
  const out: string[] = [];
  const add = (t: string) => {
    const x = t.trim();
    if (x && x !== label && !out.includes(x)) out.push(x);
  };
  const stripped = label.replace(TRAILING_QUALIFIER, "");
  add(stripped);
  for (const part of label.split(/\s\/\/\s|\s\/\/|\/\/\s/)) {
    if (part !== label) {
      add(part);
      add(part.replace(TRAILING_QUALIFIER, ""));
    }
  }
  return out;
}

/** Status fields for a set of visits: to_be_confirmed while any label waits. */
export function scheduleFormsStatusFor(
  visits: Array<{ formsToConfirm?: unknown }>,
  reasonWhenWaiting?: string,
): ScheduleFormsStatusFields {
  if (!Array.isArray(visits) || visits.length === 0) {
    return {
      scheduleFormsStatus: "no_schedule",
      scheduleFormsReason: "the study has no visit schedule yet, so there is nothing to match to forms",
    };
  }
  const waiting = (visits ?? []).reduce(
    (n, v) => n + (Array.isArray(v?.formsToConfirm) ? v.formsToConfirm.filter((x) => str(x)).length : 0),
    0,
  );
  if (waiting === 0) return { scheduleFormsStatus: "matched" };
  return {
    scheduleFormsStatus: "to_be_confirmed",
    scheduleFormsReason:
      reasonWhenWaiting ??
      `${waiting} schedule value(s) are not CRF form names yet and are waiting for a person to confirm them`,
  };
}

function uniqPush(into: string[], value: string) {
  if (!into.includes(value)) into.push(value);
}

/**
 * Re-analysis: carry the forms a person confirmed on a stored visit onto the
 * freshly extracted visit with the same (normalised) name. A carried form is no
 * longer asked about, so it is removed from the new `formsToConfirm`.
 *
 * Stored forms are carried as they are. They cannot be re-checked here (the
 * protocol pass has no CRF form list in hand), so a legacy value that is not a
 * real form is carried too, and check B5 reports it. Dropping a person's work
 * would be the worse error.
 */
export function carryConfirmedForms<T extends { name?: unknown; forms?: unknown; formsToConfirm?: unknown }>(
  fresh: T[],
  stored: Array<Record<string, any>> | null | undefined,
): T[] {
  if (!Array.isArray(stored) || stored.length === 0) return fresh;
  const byName = new Map<string, string[]>();
  for (const s of stored) {
    const k = normalizeFormName(str(s?.name));
    const forms = Array.isArray(s?.forms) ? s.forms.map(str).filter(Boolean) : [];
    if (k && forms.length && !byName.has(k)) byName.set(k, forms);
  }
  return (fresh ?? []).map((v) => {
    const kept = byName.get(normalizeFormName(str(v?.name)));
    if (!kept) return v;
    const keptKeys = new Set(kept.map(normalizeFormName));
    const toConfirm = Array.isArray(v.formsToConfirm)
      ? (v.formsToConfirm as unknown[]).map(str).filter((l) => l && !keptKeys.has(normalizeFormName(l)))
      : [];
    return { ...v, forms: [...kept], formsToConfirm: toConfirm };
  });
}

export interface RematchResult<V> {
  visits: V[];
  /** Labels moved from formsToConfirm into forms. */
  moved: number;
  /** Values already in some visit's forms that are not real CRF forms. Reported, never removed. */
  formsNotMatching: string[];
  status: ScheduleFormsStatusFields;
}

/**
 * matchScheduleFormsV2's core. Re-runs the matcher over every visit's
 * `formsToConfirm` against the current CRF forms, moving matched labels into
 * `forms` (as the real form name). Never removes anything from `forms`, and
 * never calls a model.
 */
export function rematchScheduleForms<V extends Record<string, any>>(
  visits: V[],
  forms: CrfFormRef[],
  formAliases: FormAliases | null | undefined,
): RematchResult<V> {
  const refs = formsWithAliases(forms, formAliases);
  let moved = 0;
  const notMatching: string[] = [];
  const out = (visits ?? []).map((v) => {
    const current: string[] = Array.isArray(v?.forms) ? v.forms.map(str).filter(Boolean) : [];
    for (const f of matchVisitForms(current, refs).failing) uniqPush(notMatching, f);
    const pending: unknown[] = Array.isArray(v?.formsToConfirm) ? v.formsToConfirm : [];
    const r = matchVisitForms(pending, refs);
    const nextForms = [...current];
    // How each label matched, kept so a screen can say "matched by wording" and
    // a person can untick it. Earlier records stay; one per label and form.
    const formMatches: Array<Record<string, unknown>> = Array.isArray(v?.formMatches) ? [...v.formMatches] : [];
    for (const m of r.matched) {
      moved++;
      uniqPush(nextForms, m.form);
      if (!formMatches.some((x) => x?.label === m.label && x?.form === m.form)) {
        formMatches.push({ label: m.label, form: m.form, how: m.how, ...(m.item ? { item: m.item } : {}) });
      }
    }
    const { formCandidates: _stale, ...rest } = v as Record<string, any>;
    return {
      ...(rest as V),
      forms: nextForms,
      formsToConfirm: r.failing,
      ...(formMatches.length ? { formMatches } : {}),
      ...(r.ambiguous.length ? { formCandidates: r.ambiguous } : {}),
    };
  });
  return { visits: out, moved, formsNotMatching: notMatching, status: scheduleFormsStatusFor(out) };
}


/**
 * Protocol analysis -> studyDesign/config visits (Jim, 2026-10-01).
 *
 * The protocol's schedule lists ASSESSMENTS and procedures ("Physical exam",
 * "Study drug 10 mg BID"), not CRF forms, and when the protocol is analysed
 * there usually are no CRF forms to match them against. So the model's list is
 * kept as `assessments` (raw, as read), `forms` starts empty, and every value
 * waits in `formsToConfirm` until matchScheduleFormsV2 matches it against the
 * CRF forms. This replaces copying the raw list straight into `forms`, which is
 * how dosing text reached a schedule's forms column (item 5).
 *
 * Timing rules unchanged: an absent window is 0, "none stated", never invented.
 */
export function studyDesignVisitsFromSchedule(
  scheduleVisits: Array<Record<string, any>>,
): Array<Record<string, any>> {
  return (scheduleVisits ?? []).map((v: any, i: number) => {
    const rawList: unknown[] = Array.isArray(v?.procedures)
      ? v.procedures
      : Array.isArray(v?.assessments)
        ? v.assessments
        : [];
    const assessments = rawList.map(str).filter(Boolean);
    // The schedule's own timing words ("Within 28 days before Day 1") are kept so
    // the grid can show them when a visit has no day and no window. They are
    // text only: no day number is ever worked out from them.
    const timing = str(v?.timing);
    return {
      id: `visit-${i + 1}`,
      name: v?.name || `Visit ${i + 1}`,
      // An unstated day stays null: a day is never invented from the visit's position.
      day: typeof v?.day === "number" && Number.isFinite(v.day) ? v.day : null,
      windowBefore: typeof v?.windowBefore === "number" ? v.windowBefore : 0,
      windowAfter: typeof v?.windowAfter === "number" ? v.windowAfter : 0,
      ...(timing ? { timing } : {}),
      required: true,
      assessments,
      forms: [] as string[],
      formsToConfirm: [...new Set(assessments)],
    };
  });
}

export interface ScheduleFieldsOptions {
  /** The fresh visits were read from the protocol's sentences, not a schedule table. */
  fromProtocolText?: boolean;
}

/** The provenance fields of the stored config, carried with its visits. */
function carriedVisitsProvenance(stored: Record<string, any> | null | undefined): Record<string, any> {
  const out: Record<string, any> = {};
  for (const k of ["visitsSource", "visitsConfirmedAt", "visitsConfirmedBy"]) {
    if (stored && stored[k] !== undefined) out[k] = stored[k];
  }
  return out;
}

/**
 * The schedule fields of the studyDesign/config REPLACE a protocol analysis
 * writes, computed from the stored document inside the same transaction:
 *   - an empty schedule keeps the stored visits (the Gemini 3 empty answer;
 *     unchanged from before);
 *   - otherwise the fresh visits, carrying forms a person confirmed on a stored
 *     visit of the same name (carryConfirmedForms);
 *   - the person-made `formAliases` map is carried, since the write replaces
 *     the whole document;
 *   - scheduleFormsStatus says whether anything is waiting to be confirmed.
 *
 * VISITS READ FROM THE PROTOCOL'S SENTENCES (`fromProtocolText`, 2026-11-04,
 * schedule/proseVisits.ts) only ever fill an EMPTY list: when anything is
 * stored (typed, imported, table-read, or an earlier prose read) the stored
 * visits stay, exactly as for an empty schedule. The config then says where the
 * visits came from (`visitsSource`) and that no one has confirmed them. The
 * provenance travels with the stored visits it describes, because this write
 * replaces the whole document; a table-read schedule replaces the visits and
 * the mark goes with them.
 */
export function studyDesignScheduleFields(
  visits: Array<Record<string, any>>,
  stored: Record<string, any> | null | undefined,
  opts: ScheduleFieldsOptions = {},
): {
  visits: Array<Record<string, any>>;
  formAliases?: FormAliases;
  visitsSource?: string;
  visitsConfirmedAt?: unknown;
  visitsConfirmedBy?: unknown;
} & ScheduleFormsStatusFields {
  const storedVisits = Array.isArray(stored?.visits) ? (stored!.visits as Array<Record<string, any>>) : [];
  const fillsEmpty = !!opts.fromProtocolText && visits.length > 0 && storedVisits.length === 0;
  const keepsStored = visits.length === 0 || (!!opts.fromProtocolText && storedVisits.length > 0);
  const nextVisits = keepsStored ? storedVisits : fillsEmpty ? visits : carryConfirmedForms(visits, storedVisits);
  const aliases =
    stored?.formAliases && typeof stored.formAliases === "object" && !Array.isArray(stored.formAliases)
      ? (stored.formAliases as FormAliases)
      : undefined;
  return {
    visits: nextVisits,
    ...(aliases ? { formAliases: aliases } : {}),
    ...(fillsEmpty
      ? { visitsSource: "protocol_text", visitsConfirmedAt: null, visitsConfirmedBy: null }
      : keepsStored
        ? carriedVisitsProvenance(stored)
        : {}),
    ...scheduleFormsStatusFor(
      nextVisits,
      "the protocol's schedule lists assessments, not CRF form names; they are kept to be matched to CRF forms once the forms exist",
    ),
  };
}


export interface ScheduleFormsMatchPlan {
  /**
   * no_forms: the study has no CRF forms, so nothing is written.
   * no_schedule: studyDesign/config has no visits, so nothing is written.
   * unchanged: nothing moved and the status is already right; nothing written.
   * updated: write `update`.
   */
  outcome: "no_forms" | "no_schedule" | "unchanged" | "updated";
  moved: number;
  /** Labels still waiting after this match, de-duplicated. */
  stillToConfirm: string[];
  /** Values already in forms that are not real CRF forms (left in place). */
  formsNotMatching: string[];
  update?: { visits: Array<Record<string, any>> } & ScheduleFormsStatusFields;
}

/**
 * matchScheduleFormsV2's decision, pure: re-run matching over the stored
 * visits against the current CRF forms. The callable reads the config and the
 * forms, calls this, and writes `update` when there is one. No model call.
 */
export function planScheduleFormsMatch(
  config: Record<string, any> | null | undefined,
  forms: CrfFormRef[],
): ScheduleFormsMatchPlan {
  const visits: Array<Record<string, any>> = Array.isArray(config?.visits) ? config!.visits : [];
  if (visits.length === 0) return { outcome: "no_schedule", moved: 0, stillToConfirm: [], formsNotMatching: [] };
  const realForms = (forms ?? []).filter((f) => str(f?.name));
  if (realForms.length === 0) {
    const waiting: string[] = [];
    for (const v of visits) for (const l of Array.isArray(v?.formsToConfirm) ? v.formsToConfirm : []) if (str(l)) uniqPush(waiting, str(l));
    return { outcome: "no_forms", moved: 0, stillToConfirm: waiting, formsNotMatching: [] };
  }
  const r = rematchScheduleForms(visits, realForms, config?.formAliases);
  const stillToConfirm: string[] = [];
  for (const v of r.visits) for (const l of v.formsToConfirm as string[]) uniqPush(stillToConfirm, l);
  const status: ScheduleFormsStatusFields =
    stillToConfirm.length === 0
      ? { scheduleFormsStatus: "matched" }
      : {
          scheduleFormsStatus: "to_be_confirmed",
          scheduleFormsReason:
            `${stillToConfirm.length} schedule value(s) are not CRF form names and are waiting for a person to confirm them: ` +
            stillToConfirm.join("; "),
        };
  const statusChanged =
    config?.scheduleFormsStatus !== status.scheduleFormsStatus ||
    (config?.scheduleFormsReason ?? undefined) !== status.scheduleFormsReason;
  if (r.moved === 0 && !statusChanged) {
    return { outcome: "unchanged", moved: 0, stillToConfirm, formsNotMatching: r.formsNotMatching };
  }
  return {
    outcome: "updated",
    moved: r.moved,
    stillToConfirm,
    formsNotMatching: r.formsNotMatching,
    update: { visits: r.visits, ...status },
  };
}
